import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import {
    ACCOUNT_TYPES,
    inferAccountType,
    isIndividual,
    isForProfit,
    isNPO,
    isNonProfit,
    isGeneralNonProfit
} from '../../constants';
import { formatCurrency, formatDate, getFiscalYearRange } from '../../utils';
import { exportHOT010, generateHOT010Content } from '../../utils/hot010Export';

const FmtAmt = ({ value }) => value < 0
    ? <span className="amount" style={{color: '#e74c3c'}}>△¥{formatCurrency(Math.abs(value))}</span>
    : <span className="amount">¥{formatCurrency(value)}</span>;

const BsView = ({ balanceSheetData, terminology, isGeneralNonProfitType, netAssetsChangeData, prevYearNetAssets, formatCurrency: _fc }) => {
    const equityEndBalance = isGeneralNonProfitType && netAssetsChangeData
        ? prevYearNetAssets + netAssetsChangeData.netChange
        : balanceSheetData.currentProfit;

    return (
        <div className="balance-section">
            <div className="balance-column">
                <h3>資産の部</h3>
                {balanceSheetData.assets.map((item, idx) => (
                    <div key={idx} className="balance-item">
                        <span>{item.name}</span>
                        <FmtAmt value={item.balance} />
                    </div>
                ))}
                <div className="balance-total">
                    <span>資産合計</span>
                    <FmtAmt value={balanceSheetData.totalAssets} />
                </div>
            </div>

            <div className="balance-column">
                <h3>負債の部</h3>
                {balanceSheetData.liabilities.map((item, idx) => (
                    <div key={idx} className="balance-item">
                        <span>{item.name}</span>
                        <FmtAmt value={item.balance} />
                    </div>
                ))}
                <div className="balance-total">
                    <span>負債合計</span>
                    <FmtAmt value={balanceSheetData.totalLiabilities} />
                </div>

                <h3 style={{marginTop: '30px'}}>{terminology.equitySection}</h3>
                {balanceSheetData.equity.map((item, idx) => (
                    <div key={idx} className="balance-item">
                        <span>{item.name}</span>
                        <FmtAmt value={item.balance} />
                    </div>
                ))}
                <div className="balance-item">
                    <span>{terminology.equityEndBalanceLabel}</span>
                    <FmtAmt value={equityEndBalance} />
                </div>
                <div className="balance-total">
                    <span>{terminology.totalEquityLabel}</span>
                    <FmtAmt value={balanceSheetData.totalEquity} />
                </div>

                <div className="balance-total" style={{marginTop: '20px', background: '#f8f9fa', padding: '12px', borderRadius: '4px'}}>
                    <span>{terminology.totalLiabilitiesEquityLabel}</span>
                    <FmtAmt value={balanceSheetData.totalLiabilities + balanceSheetData.totalEquity} />
                </div>
            </div>
        </div>
    );
};

// 経常外とみなす科目名リスト
const EXTRAORDINARY_ACCOUNTS = [
    '固定資産売却益', '固定資産売却損', '固定資産除却損',
    '災害損失', '過年度修正損益', '過年度修正損', '過年度修正益'
];

// 法人税計算（taxCategory に応じた税率）
const CORP_TAX_THRESHOLD = 8_000_000;
const calculateCorporateTax = (taxableIncome, taxCategory = 'nonprofit') => {
    if (taxableIncome <= 0) return 0;
    if (taxCategory === 'normal') {
        // 普通型: 法人税法66条2項（800万以下15%、超23.2%）
        return taxableIncome <= CORP_TAX_THRESHOLD
            ? Math.floor(taxableIncome * 0.15)
            : Math.floor(CORP_TAX_THRESHOLD * 0.15 + (taxableIncome - CORP_TAX_THRESHOLD) * 0.232);
    }
    // 非営利型: 法人税法66条3項（収益事業所得のみ、一律19%）
    return Math.floor(taxableIncome * 0.19);
};

const FinancialStatements = ({
    accounts,
    journals,
    getAccountBalance,
    currentFiscalYear,
    orgType,
    orgInfo,
    fileSystem,
    fiscalYears
}) => {
    const [activeTab, setActiveTab] = useState('bs');
    const [categoryFilter, setCategoryFilter] = useState('全体');
    const [copyMessage, setCopyMessage] = useState(null);
    const [prevYearNetAssets, setPrevYearNetAssets] = useState(0); // 期首正味財産残高
    const [isLoadingPrevYear, setIsLoadingPrevYear] = useState(false);
    const [isPdfExporting, setIsPdfExporting] = useState(false);
    const [pdfHeaderText, setPdfHeaderText] = useState(null);
    const [pdfCaptureWidth, setPdfCaptureWidth] = useState(null);
    const [etaxPreview, setEtaxPreview] = useState(null); // {bsContent, plContent}
    const contentRef = useRef(null);
    const isNPOType = isNPO(orgType);
    const isGeneralNonProfitType = isGeneralNonProfit(orgType);
    const showCategoryFilter = isNonProfit(orgType);

    // 前年度のデータを読み込み、正味財産残高を計算（一般社団法人のみ）
    useEffect(() => {
        const loadPreviousYearNetAssets = async () => {
            if (!isGeneralNonProfitType || !fileSystem || !currentFiscalYear) return;

            const prevYear = currentFiscalYear - 1;
            // 前年度が存在するか確認
            const prevFY = fiscalYears?.find(fy => fy.year === prevYear);
            if (!prevFY) {
                // 初年度の場合は期首残高0
                setPrevYearNetAssets(0);
                return;
            }

            setIsLoadingPrevYear(true);
            try {
                const prevData = await fileSystem.loadData(prevYear, accounts);
                if (prevData && prevData.journals) {
                    // 前年度末の正味財産残高を計算
                    // 正味財産科目（純資産=equity）の残高を集計
                    const equityAccounts = accounts.filter(a => inferAccountType(a) === ACCOUNT_TYPES.EQUITY);
                    let netAssets = 0;

                    // 前年度の各仕訳から純資産科目の残高を計算
                    prevData.journals.forEach(journal => {
                        journal.lines?.forEach(line => {
                            const account = equityAccounts.find(a => a.id === line.accountId);
                            if (account) {
                                // 純資産は貸方がプラス
                                if (line.type === 'credit') {
                                    netAssets += line.amount;
                                } else {
                                    netAssets -= line.amount;
                                }
                            }
                        });
                    });

                    // 前年度の損益（収益-費用）も正味財産に加算
                    const revenueAccounts = accounts.filter(a => inferAccountType(a) === ACCOUNT_TYPES.REVENUE);
                    const expenseAccounts = accounts.filter(a => inferAccountType(a) === ACCOUNT_TYPES.EXPENSE);
                    let prevYearRevenue = 0;
                    let prevYearExpense = 0;

                    prevData.journals.forEach(journal => {
                        journal.lines?.forEach(line => {
                            const revAccount = revenueAccounts.find(a => a.id === line.accountId);
                            if (revAccount) {
                                if (line.type === 'credit') {
                                    prevYearRevenue += line.amount;
                                } else {
                                    prevYearRevenue -= line.amount;
                                }
                            }
                            const expAccount = expenseAccounts.find(a => a.id === line.accountId);
                            if (expAccount) {
                                if (line.type === 'debit') {
                                    prevYearExpense += line.amount;
                                } else {
                                    prevYearExpense -= line.amount;
                                }
                            }
                        });
                    });

                    const prevYearProfit = prevYearRevenue - prevYearExpense;
                    setPrevYearNetAssets(netAssets + prevYearProfit);
                } else {
                    setPrevYearNetAssets(0);
                }
            } catch (err) {
                console.error('Failed to load previous year data:', err);
                setPrevYearNetAssets(0);
            } finally {
                setIsLoadingPrevYear(false);
            }
        };

        loadPreviousYearNetAssets();
    }, [isGeneralNonProfitType, fileSystem, currentFiscalYear, fiscalYears, accounts]);

    // コピー機能
    const copyToClipboard = (value) => {
        const text = String(Math.abs(value));
        navigator.clipboard.writeText(text).then(() => {
            setCopyMessage('コピーしました');
            setTimeout(() => setCopyMessage(null), 1000);
        });
    };

    const CopyableAmount = ({ value, isNegative = false }) => (
        <span
            className="amount"
            style={{
                cursor: 'pointer',
                backgroundColor: '#fffef0',
                padding: '2px 6px',
                borderRadius: '3px',
                color: isNegative ? '#e74c3c' : undefined
            }}
            onClick={() => copyToClipboard(value)}
            title="クリックでコピー"
        >
            {isNegative && value !== 0 ? '△' : ''}¥{formatCurrency(Math.abs(value))}
        </span>
    );

    // 用語の切り替え
    const terminology = useMemo(() => {
        if (isIndividual(orgType)) {
            return {
                pageTitle: '決算書',
                plTab: '損益計算書',
                equitySection: '元入金の部',
                currentProfit: '所得金額',
                equityEndBalanceLabel: '所得金額',
                totalEquityLabel: '純資産合計',
                totalLiabilitiesEquityLabel: '負債・純資産合計',
                revenueSection: '収入金額',
                expenseSection: '経費',
                totalRevenue: '収入合計',
                totalExpense: '経費合計'
            };
        } else if (isForProfit(orgType)) {
            return {
                pageTitle: '決算書',
                plTab: '損益計算書',
                equitySection: '純資産の部',
                currentProfit: '当期純利益',
                equityEndBalanceLabel: '当期純利益',
                totalEquityLabel: '純資産合計',
                totalLiabilitiesEquityLabel: '負債・純資産合計',
                revenueSection: '売上高',
                expenseSection: '販売費及び一般管理費',
                totalRevenue: '売上高計',
                totalExpense: '経費計'
            };
        } else if (isNPO(orgType)) {
            return {
                pageTitle: '決算書',
                plTab: '活動計算書',
                equitySection: '正味財産の部',
                currentProfit: '当期正味財産増減額',
                equityEndBalanceLabel: '当期正味財産増減額',
                totalEquityLabel: '正味財産合計',
                totalLiabilitiesEquityLabel: '負債及び正味財産合計',
                revenueSection: 'I 経常収益',
                expenseSection: 'II 経常費用',
                totalRevenue: '経常収益計',
                totalExpense: '経常費用計'
            };
        } else {
            return {
                pageTitle: '決算書',
                plTab: '正味財産増減計算書',
                equitySection: '正味財産の部',
                currentProfit: '当期正味財産増減額',
                equityEndBalanceLabel: '一般正味財産期末残高',
                totalEquityLabel: '正味財産合計',
                totalLiabilitiesEquityLabel: '負債及び正味財産合計',
                revenueSection: '経常収益の部',
                expenseSection: '経常費用の部',
                totalRevenue: '経常収益計',
                totalExpense: '経常費用計'
            };
        }
    }, [orgType]);

    const { start, end } = getFiscalYearRange(currentFiscalYear);

    // 貸借対照表データ
    const balanceSheetData = useMemo(() => {
        const assets = accounts
            .filter(a => inferAccountType(a) === ACCOUNT_TYPES.ASSET)
            .map(a => ({ name: a.name, balance: getAccountBalance(a.id) }))
            .filter(a => a.balance !== 0);

        const liabilities = accounts
            .filter(a => inferAccountType(a) === ACCOUNT_TYPES.LIABILITY)
            .map(a => ({ name: a.name, balance: getAccountBalance(a.id) }))
            .filter(a => a.balance !== 0);

        const equity = accounts
            .filter(a => inferAccountType(a) === ACCOUNT_TYPES.EQUITY)
            .map(a => ({ name: a.name, balance: getAccountBalance(a.id) }))
            .filter(a => a.balance !== 0);

        const totalRevenue = accounts
            .filter(a => inferAccountType(a) === ACCOUNT_TYPES.REVENUE)
            .reduce((sum, a) => sum + getAccountBalance(a.id), 0);
        const totalExpense = accounts
            .filter(a => inferAccountType(a) === ACCOUNT_TYPES.EXPENSE)
            .reduce((sum, a) => sum + getAccountBalance(a.id), 0);
        const currentProfit = totalRevenue - totalExpense;

        const totalAssets = assets.reduce((sum, a) => sum + a.balance, 0);
        const totalLiabilities = liabilities.reduce((sum, a) => sum + a.balance, 0);
        const totalEquity = equity.reduce((sum, a) => sum + a.balance, 0) + currentProfit;

        return {
            assets,
            liabilities,
            equity,
            currentProfit,
            totalAssets,
            totalLiabilities,
            totalEquity
        };
    }, [accounts, getAccountBalance]);

    // 損益計算書データ
    const incomeStatementData = useMemo(() => {
        const revenues = accounts
            .filter(a => inferAccountType(a) === ACCOUNT_TYPES.REVENUE)
            .map(a => ({ name: a.name, balance: getAccountBalance(a.id) }))
            .filter(a => a.balance !== 0);

        const expenses = accounts
            .filter(a => inferAccountType(a) === ACCOUNT_TYPES.EXPENSE)
            .map(a => ({ name: a.name, balance: getAccountBalance(a.id) }))
            .filter(a => a.balance !== 0);

        const totalRevenue = revenues.reduce((sum, a) => sum + a.balance, 0);
        const totalExpense = expenses.reduce((sum, a) => sum + a.balance, 0);
        const netIncome = totalRevenue - totalExpense;

        return {
            revenues,
            expenses,
            totalRevenue,
            totalExpense,
            netIncome
        };
    }, [accounts, getAccountBalance]);

    // カテゴリ別残高計算
    const getAccountBalanceByCategory = useCallback((accountId, category) => {
        if (category === '全体') {
            return getAccountBalance(accountId);
        }
        let balance = 0;
        const account = accounts.find(a => a.id === accountId);
        if (!account) return 0;

        (journals || []).forEach(journal => {
            const journalCategory = journal.category || '非収益';
            if (journalCategory !== category) return;

            (journal.lines || []).forEach(line => {
                if (line.accountId === accountId) {
                    if (line.type === 'debit') {
                        if (inferAccountType(account) === ACCOUNT_TYPES.ASSET || inferAccountType(account) === ACCOUNT_TYPES.EXPENSE) {
                            balance += line.amount;
                        } else {
                            balance -= line.amount;
                        }
                    } else {
                        if (inferAccountType(account) === ACCOUNT_TYPES.ASSET || inferAccountType(account) === ACCOUNT_TYPES.EXPENSE) {
                            balance -= line.amount;
                        } else {
                            balance += line.amount;
                        }
                    }
                }
            });
        });
        return balance;
    }, [accounts, journals, getAccountBalance]);

    // 一般社団/財団法人用：正味財産増減計算書データ
    const netAssetsChangeData = useMemo(() => {
        if (!isGeneralNonProfitType || !journals) return null;

        const getExpenseByExpenseCategory = (accountId, expenseCat) => {
            let total = 0;
            (journals || []).forEach(journal => {
                // categoryが収益事業フィルタに合致するか
                const journalCategory = journal.category || '非収益';
                if (categoryFilter !== '全体' && journalCategory !== categoryFilter) return;

                const journalExpCat = journal.expenseCategory || '事業費';
                if (journalExpCat !== expenseCat) return;

                journal.lines.forEach(line => {
                    if (line.accountId !== accountId) return;
                    const account = accounts.find(a => a.id === accountId);
                    if (!account) return;
                    if (inferAccountType(account) !== ACCOUNT_TYPES.EXPENSE) return;
                    if (line.type === 'debit') {
                        total += line.amount;
                    } else {
                        total -= line.amount;
                    }
                });
            });
            return total;
        };

        const revenueAccounts = accounts.filter(a => inferAccountType(a) === ACCOUNT_TYPES.REVENUE);
        const expenseAccounts = accounts.filter(a => inferAccountType(a) === ACCOUNT_TYPES.EXPENSE);

        // 経常収益（経常外を除く）
        const ordinaryRevenues = revenueAccounts
            .filter(a => !EXTRAORDINARY_ACCOUNTS.includes(a.name))
            .map(a => ({ id: a.id, name: a.name, balance: getAccountBalanceByCategory(a.id, categoryFilter) }))
            .filter(a => a.balance !== 0);
        const totalOrdinaryRevenue = ordinaryRevenues.reduce((sum, a) => sum + a.balance, 0);

        // 経常外収益
        const extraordinaryRevenues = revenueAccounts
            .filter(a => EXTRAORDINARY_ACCOUNTS.includes(a.name))
            .map(a => ({ id: a.id, name: a.name, balance: getAccountBalanceByCategory(a.id, categoryFilter) }))
            .filter(a => a.balance !== 0);
        const totalExtraordinaryRevenue = extraordinaryRevenues.reduce((sum, a) => sum + a.balance, 0);

        // 経常費用（事業費）- 経常外を除く
        const ordinaryExpenseAccounts = expenseAccounts.filter(a => !EXTRAORDINARY_ACCOUNTS.includes(a.name));
        const businessExpenses = ordinaryExpenseAccounts
            .map(a => ({ id: a.id, name: a.name, balance: getExpenseByExpenseCategory(a.id, '事業費') }))
            .filter(a => a.balance !== 0);
        const totalBusinessExpense = businessExpenses.reduce((sum, a) => sum + a.balance, 0);

        // 経常費用（管理費）- 経常外を除く
        const adminExpenses = ordinaryExpenseAccounts
            .map(a => ({ id: a.id, name: a.name, balance: getExpenseByExpenseCategory(a.id, '管理費') }))
            .filter(a => a.balance !== 0);
        const totalAdminExpense = adminExpenses.reduce((sum, a) => sum + a.balance, 0);

        const totalOrdinaryExpense = totalBusinessExpense + totalAdminExpense;

        // 経常外費用
        const extraordinaryExpenses = expenseAccounts
            .filter(a => EXTRAORDINARY_ACCOUNTS.includes(a.name))
            .map(a => ({ id: a.id, name: a.name, balance: getAccountBalanceByCategory(a.id, categoryFilter) }))
            .filter(a => a.balance !== 0);
        const totalExtraordinaryExpense = extraordinaryExpenses.reduce((sum, a) => sum + a.balance, 0);

        // 各種計算
        const ordinaryChange = totalOrdinaryRevenue - totalOrdinaryExpense;
        const extraordinaryChange = totalExtraordinaryRevenue - totalExtraordinaryExpense;
        const preTaxNetChange = ordinaryChange + extraordinaryChange; // 税引前当期一般正味財産増減額

        // 収益事業の所得を計算（categoryが「収益」の仕訳のみ）
        const getRevenueBusinessIncome = () => {
            let revenueBusinessRevenue = 0;
            let revenueBusinessExpense = 0;

            (journals || []).forEach(journal => {
                if (journal.category !== '収益') return;

                journal.lines?.forEach(line => {
                    const account = accounts.find(a => a.id === line.accountId);
                    if (!account) return;

                    const accountType = inferAccountType(account);
                    if (accountType === ACCOUNT_TYPES.REVENUE) {
                        if (line.type === 'credit') {
                            revenueBusinessRevenue += line.amount;
                        } else {
                            revenueBusinessRevenue -= line.amount;
                        }
                    } else if (accountType === ACCOUNT_TYPES.EXPENSE) {
                        if (line.type === 'debit') {
                            revenueBusinessExpense += line.amount;
                        } else {
                            revenueBusinessExpense -= line.amount;
                        }
                    }
                });
            });

            return revenueBusinessRevenue - revenueBusinessExpense;
        };

        const revenueBusinessIncome = getRevenueBusinessIncome();
        const taxCategory = fiscalYears?.find(fy => fy.year === currentFiscalYear)?.taxCategory ?? 'nonprofit';
        const corporateTax = calculateCorporateTax(revenueBusinessIncome, taxCategory);
        const netChange = preTaxNetChange - corporateTax; // 当期一般正味財産増減額

        return {
            ordinaryRevenues,
            totalOrdinaryRevenue,
            businessExpenses,
            totalBusinessExpense,
            adminExpenses,
            totalAdminExpense,
            totalOrdinaryExpense,
            ordinaryChange,
            extraordinaryRevenues,
            totalExtraordinaryRevenue,
            extraordinaryExpenses,
            totalExtraordinaryExpense,
            extraordinaryChange,
            preTaxNetChange,
            revenueBusinessIncome,
            corporateTax,
            netChange
        };
    }, [accounts, journals, isGeneralNonProfitType, categoryFilter, getAccountBalanceByCategory]);

    // NPO法人用：活動計算書データ（事業費/管理費別）
    const activityStatementData = useMemo(() => {
        if (!isNPOType || !journals) return null;

        const getExpenseByCategory = (accountId, expenseCat) => {
            let total = 0;
            (journals || []).forEach(journal => {
                const journalExpCat = journal.expenseCategory || '事業費';
                if (journalExpCat !== expenseCat) return;
                journal.lines.forEach(line => {
                    if (line.accountId !== accountId) return;
                    const account = accounts.find(a => a.id === accountId);
                    if (!account) return;
                    if (inferAccountType(account) !== ACCOUNT_TYPES.EXPENSE) return;
                    if (line.type === 'debit') {
                        total += line.amount;
                    } else {
                        total -= line.amount;
                    }
                });
            });
            return total;
        };

        const expenseAccounts = accounts.filter(a => inferAccountType(a) === ACCOUNT_TYPES.EXPENSE);

        const businessExpenses = expenseAccounts
            .map(a => ({ name: a.name, balance: getExpenseByCategory(a.id, '事業費') }))
            .filter(a => a.balance !== 0);
        const totalBusinessExpense = businessExpenses.reduce((sum, a) => sum + a.balance, 0);

        const adminExpenses = expenseAccounts
            .map(a => ({ name: a.name, balance: getExpenseByCategory(a.id, '管理費') }))
            .filter(a => a.balance !== 0);
        const totalAdminExpense = adminExpenses.reduce((sum, a) => sum + a.balance, 0);

        return {
            businessExpenses,
            adminExpenses,
            totalBusinessExpense,
            totalAdminExpense,
            totalExpense: totalBusinessExpense + totalAdminExpense
        };
    }, [accounts, journals, isNPOType]);

    // PDF出力
    const exportToPDF = async () => {
        if (isPdfExporting) return;
        setIsPdfExporting(true);

        try {
            const { jsPDF } = window.jspdf;
            const doc = new jsPDF('p', 'mm', 'a4');
            const pageW = doc.internal.pageSize.getWidth();
            const pageH = doc.internal.pageSize.getHeight();
            const margin = 8;
            const contentW = pageW - margin * 2;

            // stateでヘッダーと幅を制御してキャプチャ（DOM直接操作を避けReactと干渉しない）
            // コンテナ幅を2/3に縮小することでPDF上の文字サイズを1.5倍にする
            const captureWithHeader = async (title) => {
                await new Promise(resolve => setTimeout(resolve, 200));
                if (!contentRef.current) return null;
                const name = orgInfo?.name || '';
                const targetWidth = Math.round(contentRef.current.offsetWidth / 1.5);
                setPdfHeaderText(`${name}　${currentFiscalYear}年度　${title}`);
                setPdfCaptureWidth(targetWidth);
                await new Promise(resolve => setTimeout(resolve, 300));
                const canvas = await window.html2canvas(contentRef.current, {
                    scale: 2,
                    useCORS: true,
                    logging: false,
                    backgroundColor: '#ffffff'
                });
                setPdfHeaderText(null);
                setPdfCaptureWidth(null);
                return canvas;
            };

            const addCanvasToDoc = (canvas, isFirstPage) => {
                if (!isFirstPage) doc.addPage();
                const imgData = canvas.toDataURL('image/jpeg', 0.95);
                const imgW = contentW;
                const imgH = (canvas.height / canvas.width) * imgW;
                const maxH = pageH - margin * 2;

                if (imgH <= maxH) {
                    doc.addImage(imgData, 'JPEG', margin, margin, imgW, imgH);
                } else {
                    // ページをまたぐ場合は分割
                    const pxPerMm = canvas.width / imgW;
                    const pageHeightPx = maxH * pxPerMm;
                    let yPx = 0;
                    let firstChunk = true;
                    while (yPx < canvas.height) {
                        if (!firstChunk) doc.addPage();
                        const chunkH = Math.min(pageHeightPx, canvas.height - yPx);
                        const tmpCanvas = document.createElement('canvas');
                        tmpCanvas.width = canvas.width;
                        tmpCanvas.height = chunkH;
                        tmpCanvas.getContext('2d').drawImage(canvas, 0, -yPx);
                        const chunkData = tmpCanvas.toDataURL('image/jpeg', 0.95);
                        doc.addImage(chunkData, 'JPEG', margin, margin, imgW, (chunkH / canvas.width) * imgW);
                        yPx += pageHeightPx;
                        firstChunk = false;
                    }
                }
            };

            // 貸借対照表
            setActiveTab('bs');
            const bsCanvas = await captureWithHeader('貸借対照表');
            if (bsCanvas) addCanvasToDoc(bsCanvas, true);

            // 損益計算書 / 正味財産増減計算書 / 活動計算書
            setActiveTab('pl');
            const plCanvas = await captureWithHeader(terminology.plTab);
            if (plCanvas) addCanvasToDoc(plCanvas, false);

            setActiveTab('bs');
            const safeName = (orgInfo?.name || '').replace(/[\\/:*?"<>|]/g, '');
            doc.save(`${safeName}_${currentFiscalYear}年度_決算書.pdf`);
        } finally {
            setIsPdfExporting(false);
        }
    };

    // e-Tax HOT010 CSVプレビュー表示
    const openEtaxPreview = () => {
        const content = generateHOT010Content({
            orgName: orgInfo?.name || '',
            fiscalYear: currentFiscalYear,
            accounts,
            getAccountBalance,
            orgType,
        });
        setEtaxPreview(content);
    };

    // e-Tax HOT010 CSVダウンロード
    const downloadEtaxCsv = () => {
        exportHOT010({
            orgName: orgInfo?.name || '',
            fiscalYear: currentFiscalYear,
            accounts,
            getAccountBalance,
            orgType,
        });
    };

    // e-Tax CSV出力ボタンを表示するか（個人・NPOは対象外）
    const showEtaxExport = !isIndividual(orgType) && !isNPO(orgType);

    // CSVの行を読みやすく整形（ヘッダー行と科目行を区別）
    const renderCsvPreview = (content, label) => {
        if (!content) return null;
        const lines = content.split('\r\n').filter(l => l.trim());
        return (
            <div style={{marginBottom: '24px'}}>
                <div style={{fontWeight: 'bold', fontSize: '16px', marginBottom: '8px', borderBottom: '2px solid #333', paddingBottom: '4px'}}>{label}</div>
                <table style={{width: '100%', borderCollapse: 'collapse', fontSize: '13px'}}>
                    <thead>
                        <tr style={{background: '#f0f0f0'}}>
                            <th style={{padding: '4px 8px', textAlign: 'left', border: '1px solid #ccc', width: '40%'}}>科目名</th>
                            <th style={{padding: '4px 8px', textAlign: 'right', border: '1px solid #ccc', width: '20%'}}>金額</th>
                            <th style={{padding: '4px 8px', textAlign: 'center', border: '1px solid #ccc', width: '10%'}}>行区分</th>
                            <th style={{padding: '4px 8px', textAlign: 'center', border: '1px solid #ccc', width: '10%'}}>階層</th>
                            <th style={{padding: '4px 8px', textAlign: 'left', border: '1px solid #ccc', width: '20%'}}>科目コード</th>
                        </tr>
                    </thead>
                    <tbody>
                        {lines.map((line, i) => {
                            const cols = line.split(',');
                            // ヘッダー行（A/B/C1/C2）はスキップ
                            if (['A','B','C1','C2'].includes(cols[0])) return null;
                            const isTitle = cols[2] === 'T';
                            const indent = cols[3] ? (parseInt(cols[3]) - 2) * 16 : 0;
                            const amt = cols[1] ? Number(cols[1]).toLocaleString() : '';
                            return (
                                <tr key={i} style={{background: isTitle ? '#f8f8f8' : 'white'}}>
                                    <td style={{padding: '3px 8px', border: '1px solid #e0e0e0', paddingLeft: `${8 + indent}px`, fontWeight: isTitle ? 'bold' : 'normal'}}>{cols[0]}</td>
                                    <td style={{padding: '3px 8px', border: '1px solid #e0e0e0', textAlign: 'right', color: cols[1] && Number(cols[1]) < 0 ? '#e74c3c' : 'inherit'}}>{amt ? `¥${amt}` : ''}</td>
                                    <td style={{padding: '3px 8px', border: '1px solid #e0e0e0', textAlign: 'center', color: '#888'}}>{cols[2]}</td>
                                    <td style={{padding: '3px 8px', border: '1px solid #e0e0e0', textAlign: 'center', color: '#888'}}>{cols[3]}</td>
                                    <td style={{padding: '3px 8px', border: '1px solid #e0e0e0', color: '#888', fontSize: '11px'}}>{cols[4]}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        );
    };

    return (
        <div>
            {etaxPreview && (
                <div style={{position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '20px', overflowY: 'auto'}}>
                    <div style={{background: '#fff', borderRadius: '8px', width: '100%', maxWidth: '800px', padding: '24px', maxHeight: '90vh', overflowY: 'auto'}}>
                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px'}}>
                            <h3 style={{margin: 0}}>e-Tax CSV プレビュー（HOT010 Ver3.0）</h3>
                            <button className="btn btn-secondary" onClick={() => setEtaxPreview(null)}>閉じる</button>
                        </div>
                        {renderCsvPreview(etaxPreview.bsContent, '貸借対照表')}
                        {renderCsvPreview(etaxPreview.plContent, '損益計算書')}
                        <div style={{display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '16px'}}>
                            <button className="btn btn-secondary" onClick={() => setEtaxPreview(null)}>閉じる</button>
                            <button className="btn btn-primary" onClick={() => { downloadEtaxCsv(); setEtaxPreview(null); }}>ダウンロード</button>
                        </div>
                    </div>
                </div>
            )}
            {copyMessage && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 9999,
                    pointerEvents: 'none'
                }}>
                    <div style={{
                        backgroundColor: 'rgba(0, 0, 0, 0.8)',
                        color: '#fff',
                        padding: '20px 40px',
                        borderRadius: '8px',
                        fontSize: '16px',
                        fontWeight: 'bold'
                    }}>
                        {copyMessage}
                    </div>
                </div>
            )}

            <div className="page-header">
                <h2>{terminology.pageTitle}</h2>
            </div>

            <div className="card">
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '10px'}}>
                    <div className="tab-container" style={{borderBottom: 'none', marginBottom: 0}}>
                        <div className="tab-list">
                            <div
                                className={`tab-item ${activeTab === 'bs' ? 'active' : ''}`}
                                onClick={() => setActiveTab('bs')}
                            >
                                貸借対照表
                            </div>
                            <div
                                className={`tab-item ${activeTab === 'pl' ? 'active' : ''}`}
                                onClick={() => setActiveTab('pl')}
                            >
                                {terminology.plTab}
                            </div>
                        </div>
                    </div>
                    <div style={{display: 'flex', gap: '8px', alignItems: 'center'}}>
                        {showCategoryFilter && (
                            <div style={{ display: 'flex', gap: '4px' }}>
                                {['全体', '非収益', '収益', '共通'].map(cat => (
                                    <button
                                        key={cat}
                                        className={`btn ${categoryFilter === cat ? 'btn-primary' : 'btn-secondary'}`}
                                        onClick={() => setCategoryFilter(cat)}
                                        style={{
                                            padding: '6px 12px',
                                            fontSize: '12px',
                                            backgroundColor: categoryFilter === cat
                                                ? (cat === '収益' ? '#2e7d32' : cat === '共通' ? '#e65100' : undefined)
                                                : (cat === '収益' ? '#e8f5e9' : cat === '共通' ? '#fff3e0' : undefined),
                                            borderColor: cat === '収益' ? '#2e7d32' : cat === '共通' ? '#e65100' : undefined,
                                            color: categoryFilter === cat ? '#fff' : (cat === '収益' ? '#2e7d32' : cat === '共通' ? '#e65100' : undefined)
                                        }}
                                    >
                                        {cat}
                                    </button>
                                ))}
                            </div>
                        )}
                        {showEtaxExport && (
                            <button className="btn btn-secondary" onClick={openEtaxPreview} title="e-Tax用 HOT010 Ver3.0 CSV（BS/PLの2ファイル）">
                                e-Tax CSV出力
                            </button>
                        )}
                        <button className="btn btn-primary" onClick={exportToPDF} disabled={isPdfExporting}>
                            {isPdfExporting ? '出力中...' : 'PDF出力'}
                        </button>
                    </div>
                </div>

                <div ref={contentRef} style={pdfCaptureWidth ? {width: `${pdfCaptureWidth}px`, background: '#fff'} : {}}>
                {pdfHeaderText && (
                    <div style={{padding: '10px 0', fontSize: '26px', fontWeight: 'bold', fontFamily: 'sans-serif', textAlign: 'center', borderBottom: '1px solid #aaa', marginBottom: '8px', background: '#fff'}}>
                        {pdfHeaderText}
                    </div>
                )}
                <div style={{textAlign: 'center', marginBottom: '20px', color: '#666'}}>
                    {currentFiscalYear}年度（{formatDate(start)}〜{formatDate(end)}）
                    {showCategoryFilter && categoryFilter !== '全体' && (
                        <span style={{
                            marginLeft: '10px',
                            padding: '2px 8px',
                            borderRadius: '4px',
                            backgroundColor: categoryFilter === '収益' ? '#e8f5e9' : categoryFilter === '共通' ? '#fff3e0' : '#f5f5f5',
                            color: categoryFilter === '収益' ? '#2e7d32' : categoryFilter === '共通' ? '#e65100' : '#666'
                        }}>
                            {categoryFilter}事業のみ
                        </span>
                    )}
                </div>

                {activeTab === 'bs' ? (
                    <BsView
                        balanceSheetData={balanceSheetData}
                        terminology={terminology}
                        isGeneralNonProfitType={isGeneralNonProfitType}
                        netAssetsChangeData={netAssetsChangeData}
                        prevYearNetAssets={prevYearNetAssets}
                        formatCurrency={formatCurrency}
                    />
                ) : isGeneralNonProfitType && netAssetsChangeData ? (
                    <div>
                        {/* Ⅰ 経常増減の部 */}
                        <h3 style={{marginBottom: '16px', paddingBottom: '8px', borderBottom: '2px solid #333', fontWeight: 'bold'}}>
                            Ⅰ 経常増減の部
                        </h3>

                        {/* 1. 経常収益 */}
                        <h4 style={{marginTop: '16px', marginBottom: '12px', paddingLeft: '16px', fontWeight: 'bold'}}>
                            1. 経常収益
                        </h4>
                        {netAssetsChangeData.ordinaryRevenues.map((item, idx) => (
                            <div key={idx} className="balance-item" style={{paddingLeft: '32px'}}>
                                <span>{item.name}</span>
                                <CopyableAmount value={item.balance} />
                            </div>
                        ))}
                        <div className="balance-total" style={{marginTop: '8px', paddingLeft: '16px'}}>
                            <span>経常収益計</span>
                            <CopyableAmount value={netAssetsChangeData.totalOrdinaryRevenue} />
                        </div>

                        {/* 2. 経常費用 */}
                        <h4 style={{marginTop: '24px', marginBottom: '12px', paddingLeft: '16px', fontWeight: 'bold'}}>
                            2. 経常費用
                        </h4>

                        {/* (1) 事業費 */}
                        <h5 style={{marginTop: '12px', marginBottom: '8px', paddingLeft: '24px', color: '#2e7d32'}}>
                            (1) 事業費
                        </h5>
                        {netAssetsChangeData.businessExpenses.map((item, idx) => (
                            <div key={idx} className="balance-item" style={{paddingLeft: '40px'}}>
                                <span>{item.name}</span>
                                <CopyableAmount value={item.balance} />
                            </div>
                        ))}
                        <div className="balance-total" style={{background: '#e8f5e9', padding: '8px 12px', marginTop: '8px', marginLeft: '24px'}}>
                            <span>事業費計</span>
                            <CopyableAmount value={netAssetsChangeData.totalBusinessExpense} />
                        </div>

                        {/* (2) 管理費 */}
                        <h5 style={{marginTop: '16px', marginBottom: '8px', paddingLeft: '24px', color: '#1565c0'}}>
                            (2) 管理費
                        </h5>
                        {netAssetsChangeData.adminExpenses.map((item, idx) => (
                            <div key={idx} className="balance-item" style={{paddingLeft: '40px'}}>
                                <span>{item.name}</span>
                                <CopyableAmount value={item.balance} />
                            </div>
                        ))}
                        <div className="balance-total" style={{background: '#e3f2fd', padding: '8px 12px', marginTop: '8px', marginLeft: '24px'}}>
                            <span>管理費計</span>
                            <CopyableAmount value={netAssetsChangeData.totalAdminExpense} />
                        </div>

                        <div className="balance-total" style={{marginTop: '16px', paddingLeft: '16px'}}>
                            <span>経常費用計</span>
                            <CopyableAmount value={netAssetsChangeData.totalOrdinaryExpense} />
                        </div>

                        {/* 当期経常増減額 */}
                        <div className="balance-total" style={{marginTop: '24px', background: '#e8f4fd', padding: '12px', borderRadius: '4px'}}>
                            <span style={{fontWeight: 'bold'}}>当期経常増減額</span>
                            <CopyableAmount value={netAssetsChangeData.ordinaryChange} isNegative={netAssetsChangeData.ordinaryChange < 0} />
                        </div>

                        {/* Ⅱ 経常外増減の部 */}
                        <h3 style={{marginTop: '32px', marginBottom: '16px', paddingBottom: '8px', borderBottom: '2px solid #333', fontWeight: 'bold'}}>
                            Ⅱ 経常外増減の部
                        </h3>

                        {/* 1. 経常外収益 */}
                        <h4 style={{marginTop: '16px', marginBottom: '12px', paddingLeft: '16px', fontWeight: 'bold'}}>
                            1. 経常外収益
                        </h4>
                        {netAssetsChangeData.extraordinaryRevenues.length > 0 ? (
                            netAssetsChangeData.extraordinaryRevenues.map((item, idx) => (
                                <div key={idx} className="balance-item" style={{paddingLeft: '32px'}}>
                                    <span>{item.name}</span>
                                    <CopyableAmount value={item.balance} />
                                </div>
                            ))
                        ) : (
                            <div style={{paddingLeft: '32px', color: '#999'}}>（該当なし）</div>
                        )}
                        <div className="balance-total" style={{marginTop: '8px', paddingLeft: '16px'}}>
                            <span>経常外収益計</span>
                            <CopyableAmount value={netAssetsChangeData.totalExtraordinaryRevenue} />
                        </div>

                        {/* 2. 経常外費用 */}
                        <h4 style={{marginTop: '16px', marginBottom: '12px', paddingLeft: '16px', fontWeight: 'bold'}}>
                            2. 経常外費用
                        </h4>
                        {netAssetsChangeData.extraordinaryExpenses.length > 0 ? (
                            netAssetsChangeData.extraordinaryExpenses.map((item, idx) => (
                                <div key={idx} className="balance-item" style={{paddingLeft: '32px'}}>
                                    <span>{item.name}</span>
                                    <CopyableAmount value={item.balance} />
                                </div>
                            ))
                        ) : (
                            <div style={{paddingLeft: '32px', color: '#999'}}>（該当なし）</div>
                        )}
                        <div className="balance-total" style={{marginTop: '8px', paddingLeft: '16px'}}>
                            <span>経常外費用計</span>
                            <CopyableAmount value={netAssetsChangeData.totalExtraordinaryExpense} />
                        </div>

                        {/* 当期経常外増減額 */}
                        <div className="balance-total" style={{marginTop: '16px'}}>
                            <span>当期経常外増減額</span>
                            <CopyableAmount value={netAssetsChangeData.extraordinaryChange} isNegative={netAssetsChangeData.extraordinaryChange < 0} />
                        </div>

                        {/* 税引前当期一般正味財産増減額 */}
                        <div className="balance-total" style={{marginTop: '32px', background: '#fff3e0', padding: '12px', borderRadius: '4px'}}>
                            <span style={{fontWeight: 'bold'}}>税引前当期一般正味財産増減額</span>
                            <CopyableAmount value={netAssetsChangeData.preTaxNetChange} isNegative={netAssetsChangeData.preTaxNetChange < 0} />
                        </div>

                        {/* 法人税、住民税及び事業税 */}
                        <div className="balance-total" style={{marginTop: '8px', background: '#ffebee', padding: '12px', borderRadius: '4px'}}>
                            <span>
                                法人税、住民税及び事業税
                                {netAssetsChangeData.revenueBusinessIncome > 0 && (
                                    <span style={{fontSize: '12px', color: '#666', marginLeft: '8px'}}>
                                        （収益事業所得: ¥{formatCurrency(netAssetsChangeData.revenueBusinessIncome)}）
                                    </span>
                                )}
                            </span>
                            <CopyableAmount value={netAssetsChangeData.corporateTax} />
                        </div>

                        {/* 当期一般正味財産増減額 */}
                        <div className="balance-total" style={{marginTop: '16px', fontSize: '18px', background: '#d4edda', padding: '16px', borderRadius: '4px'}}>
                            <span style={{fontWeight: 'bold'}}>当期一般正味財産増減額</span>
                            <CopyableAmount value={netAssetsChangeData.netChange} isNegative={netAssetsChangeData.netChange < 0} />
                        </div>

                        {/* 一般正味財産期首残高 */}
                        <div className="balance-total" style={{marginTop: '24px', background: '#f5f5f5', padding: '12px', borderRadius: '4px'}}>
                            <span>一般正味財産期首残高</span>
                            {isLoadingPrevYear ? (
                                <span style={{color: '#999'}}>読込中...</span>
                            ) : (
                                <CopyableAmount value={prevYearNetAssets} isNegative={prevYearNetAssets < 0} />
                            )}
                        </div>

                        {/* 一般正味財産期末残高 */}
                        <div className="balance-total" style={{marginTop: '8px', fontSize: '18px', background: '#e8eaf6', padding: '16px', borderRadius: '4px', borderLeft: '4px solid #3f51b5'}}>
                            <span style={{fontWeight: 'bold'}}>一般正味財産期末残高</span>
                            {isLoadingPrevYear ? (
                                <span style={{color: '#999'}}>読込中...</span>
                            ) : (
                                <CopyableAmount
                                    value={prevYearNetAssets + netAssetsChangeData.netChange}
                                    isNegative={(prevYearNetAssets + netAssetsChangeData.netChange) < 0}
                                />
                            )}
                        </div>
                    </div>
                ) : isNPOType && activityStatementData ? (
                    <div>
                        <h3 style={{marginBottom: '16px', paddingBottom: '8px', borderBottom: '2px solid #333'}}>
                            {terminology.revenueSection}
                        </h3>
                        {incomeStatementData.revenues.map((item, idx) => (
                            <div key={idx} className="balance-item">
                                <span style={{paddingLeft: '16px'}}>{item.name}</span>
                                <span className="amount">¥{formatCurrency(item.balance)}</span>
                            </div>
                        ))}
                        <div className="balance-total">
                            <span>{terminology.totalRevenue}</span>
                            <span className="amount">¥{formatCurrency(incomeStatementData.totalRevenue)}</span>
                        </div>

                        <h3 style={{marginTop: '30px', marginBottom: '16px', paddingBottom: '8px', borderBottom: '2px solid #333'}}>
                            {terminology.expenseSection}
                        </h3>

                        <h4 style={{marginTop: '16px', marginBottom: '12px', color: '#2e7d32'}}>1. 事業費</h4>
                        {activityStatementData.businessExpenses.map((item, idx) => (
                            <div key={idx} className="balance-item" style={{paddingLeft: '16px'}}>
                                <span>{item.name}</span>
                                <span className="amount">¥{formatCurrency(item.balance)}</span>
                            </div>
                        ))}
                        <div className="balance-total" style={{background: '#e8f5e9', padding: '8px 12px', marginTop: '8px'}}>
                            <span>事業費計</span>
                            <span className="amount">¥{formatCurrency(activityStatementData.totalBusinessExpense)}</span>
                        </div>

                        <h4 style={{marginTop: '20px', marginBottom: '12px', color: '#1565c0'}}>2. 管理費</h4>
                        {activityStatementData.adminExpenses.map((item, idx) => (
                            <div key={idx} className="balance-item" style={{paddingLeft: '16px'}}>
                                <span>{item.name}</span>
                                <span className="amount">¥{formatCurrency(item.balance)}</span>
                            </div>
                        ))}
                        <div className="balance-total" style={{background: '#e3f2fd', padding: '8px 12px', marginTop: '8px'}}>
                            <span>管理費計</span>
                            <span className="amount">¥{formatCurrency(activityStatementData.totalAdminExpense)}</span>
                        </div>

                        <div className="balance-total" style={{marginTop: '16px'}}>
                            <span>{terminology.totalExpense}</span>
                            <span className="amount">¥{formatCurrency(activityStatementData.totalExpense)}</span>
                        </div>

                        <div className="balance-total" style={{marginTop: '30px', fontSize: '18px', background: '#f8f9fa', padding: '16px', borderRadius: '4px'}}>
                            <span>{terminology.currentProfit}</span>
                            <span className="amount" style={{color: incomeStatementData.netIncome >= 0 ? '#27ae60' : '#e74c3c'}}>
                                ¥{formatCurrency(incomeStatementData.netIncome)}
                            </span>
                        </div>
                    </div>
                ) : (
                    <div>
                        <h3 style={{marginBottom: '16px', paddingBottom: '8px', borderBottom: '2px solid #333'}}>
                            {terminology.revenueSection}
                        </h3>
                        {incomeStatementData.revenues.map((item, idx) => (
                            <div key={idx} className="balance-item">
                                <span>{item.name}</span>
                                <span className="amount">¥{formatCurrency(item.balance)}</span>
                            </div>
                        ))}
                        <div className="balance-total">
                            <span>{terminology.totalRevenue}</span>
                            <span className="amount">¥{formatCurrency(incomeStatementData.totalRevenue)}</span>
                        </div>

                        <h3 style={{marginTop: '30px', marginBottom: '16px', paddingBottom: '8px', borderBottom: '2px solid #333'}}>
                            {terminology.expenseSection}
                        </h3>
                        {incomeStatementData.expenses.map((item, idx) => (
                            <div key={idx} className="balance-item">
                                <span>{item.name}</span>
                                <span className="amount">¥{formatCurrency(item.balance)}</span>
                            </div>
                        ))}
                        <div className="balance-total">
                            <span>{terminology.totalExpense}</span>
                            <span className="amount">¥{formatCurrency(incomeStatementData.totalExpense)}</span>
                        </div>

                        <div className="balance-total" style={{marginTop: '30px', fontSize: '18px', background: '#f8f9fa', padding: '16px', borderRadius: '4px'}}>
                            <span>{terminology.currentProfit}</span>
                            <span className="amount" style={{color: incomeStatementData.netIncome >= 0 ? '#27ae60' : '#e74c3c'}}>
                                ¥{formatCurrency(incomeStatementData.netIncome)}
                            </span>
                        </div>
                    </div>
                )}
                </div>
            </div>
        </div>
    );
};

export default FinancialStatements;
